<template>
  <div class="comment">
    <AvatarPlay
      size="4xl"
      :name="autor.name"
      :src="autor.picture"
    />

    <div class="comment__autor">
      <div class="comment__autor__info">
        <TextPlay
          :skeleton="isLoading"
          :text="autor.name"
          color="var(--fontcolor)"
          weight="semibold"
          size="sm"
        />
        <TextPlay
          :skeleton="isLoading"
          :text="timeAgo(createdAt)"
          color="var(--text-color)"
          size="xs"
        />

        <DropdownPlay
          v-if="isMine || permission"
          class="tw:ml-auto"
          :options="commentMenuOptions"
          position="bottom"
          @select="handleMenuSelect"
        >
          <template #trigger>
            <IconGoogle
              class="comment__autor__info__more"
              name="more_vert"
              color="var(--text-color)"
              size="16px"
            />
          </template>
        </DropdownPlay>
      </div>

      <div class="comment__autor__content">
        <div
          v-if="textAreaEdit"
          class="comment__autor__content__to-edit"
        >
          <TextareaPlayV2
            v-model="editMyComment"
            :disabled="isLoading"
            :placeholder="t('player.comment_placeholder')"
            :maxlength="1000"
            @click="isTextAreaToEditFocused = true"
          />
          <div
            v-if="isTextAreaToEditFocused"
            class="comment__autor__content__to-edit__actions"
          >
            <ButtonPlayV2
              :disabled="isLoading"
              :text="t('comments.to_cancel')"
              variant="ghost"
              @click="handleCancelCommentEdition"
            />
            <ButtonPlayV2
              :text="t('comments.to_update')"
              variant="filled"
              :loading="isLoading"
              :disabled="!editMyComment || isLoading"
              @click="editMyCommentFunc(id)"
            />
          </div>
        </div>

        <!-- eslint-disable vue/no-v-text-v-html-on-component vue/no-v-html -->
        <TextPlay
          v-if="!textAreaEdit"
          :skeleton="isLoading"
          color="var(--fontcolor)"
          size="sm"
          v-html="commentTruncated"
        />
        <!-- eslint-enable vue/no-v-text-v-html-on-component vue/no-v-html -->
        <TextPlay
          v-if="!(commentTruncated.length >= label.length) && !textAreaEdit"
          :skeleton="isLoading"
          class="comment__autor__content__show-more"
          :text="t('comments.to_read_more')"
          color="var(--text-color)"
          weight="semibold"
          size="sm"
          @click="showMore"
        />
        <TextPlay
          v-if="commentTruncated.length - 3 > 200 && !textAreaEdit"
          :skeleton="isLoading"
          class="comment__autor__content__show-less"
          :text="t('comments.to_read_less')"
          color="var(--text-color)"
          weight="semibold"
          size="sm"
          @click="showLess"
        />

        <div class="comment__autor__content__actions">
          <div v-if="!deletedAt && status == 'published'" class="likes">
            <IconGoogle
              :color="liked ? 'var(--fontcolor)' : 'var(--text-color)'"
              :fill="liked"
              name="thumb_up"
              size="22px"
              @click="
                liked
                  ? doCommentUnlike({ id, courseId, moduleId, lessonId })
                  : doCommentLike({ id, courseId, moduleId, lessonId })
              "
            />
            <TextPlay
              :skeleton="isLoading"
              :text="likesCount"
              color="var(--text-color)"
              size="xs"
            />
          </div>

          <TextPlay
            v-if="!deletedAt && !textAreaEdit"
            :skeleton="isLoading"
            class="to-answer tw:inline-block tw:py-2 tw:px-4"
            :text="t('comments.to_answer')"
            color="var(--fontcolor)"
            size="xs"
            weight="semibold"
            @click="toggleToAnswer"
          />

          <TextPlay
            v-if="permission && (status === 'draft' || status === 'reproved') && !deletedAt"
            :skeleton="isLoading"
            class="to-answer tw:inline-block tw:py-2 tw:px-4"
            :text="t('comments.to_approve')"
            color="var(--fontcolor)"
            size="xs"
            weight="semibold"
            @click="doCommentApprove({ id, courseId, moduleId, lessonId })"
          />

          <TextPlay
            v-if="permission && (status === 'draft' || status === 'published') && !deletedAt"
            :skeleton="isLoading"
            class="to-answer tw:inline-block tw:py-2 tw:px-4"
            :text="t('comments.to_reprove')"
            color="var(--fontcolor)"
            size="xs"
            weight="semibold"
            @click="doCommentReprove({ id })"
          />
        </div>

        <div
          v-if="textAreaResp && !textAreaEdit"
          class="comment__autor__content__to-answer"
        >
          <TextareaPlayV2
            v-model="myAnswer"
            class="tw:mb-2"
            :disabled="isLoading"
            :placeholder="t('player.comment_placeholder')"
            :maxlength="1000"
            @click="isTextAreaMyAnswerFocused = true"
          />
          <div
            v-if="isTextAreaMyAnswerFocused"
            class="comment__autor__content__to-answer__actions"
          >
            <ButtonPlayV2
              :disabled="isLoading"
              :text="t('comments.to_cancel')"
              variant="ghost"
              @click="handleCancelMyAnswer"
            />
            <ButtonPlayV2
              :text="t('comments.to_answer')"
              variant="filled"
              :loading="isLoading"
              :disabled="!myAnswer || isLoading"
              @click="answer(id)"
            />
          </div>
        </div>

        <div class="comment__autor__content__replies">
          <div
            v-if="nodes?.length > 0"
            class="see-replies"
            :class="{ 'tw:mb-2': showReplies }"
            @click="toggleShowReplies"
          >
            <IconGoogle
              :color="'var(--maincolor)'"
              :name="showReplies ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
              size="14px"
            />
            <TextPlay
              :skeleton="isLoading"
              size="sm"
              color="var(--maincolor)"
              :text="t('comments.replies_count', nodes?.length, { count: nodes?.length })"
            />
          </div>

          <template v-for="(replie, index) in nodes">
            <div
              v-if="showReplies"
              :key="index"
              class="replies"
            >
              <WatchLessonCommentsItem
                v-if="replie.status != 'reproved'"
                :id="replie.id"
                :key="replie.id"
                :label="replie.comment"
                :autor="replie.member"
                :created-at="replie.created_at"
                :updated-at="replie.updated_at"
                :is-mine="replie.is_mine"
                :status="replie.status"
                :liked="replie.liked"
                :likes-count="replie.likes_count"
                :nodes="replie.replies"
                :depth="0"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { modal, toast } from '@/events'
import { formatDistanceToNow } from 'date-fns'
import { ptBR, enUS, es } from 'date-fns/locale'
import Cookies from 'js-cookie'
import { useWatchStore } from '@/store/watch.store'
import { TextareaPlayV2, DropdownPlay } from "@/components/atomic"


defineOptions({ name: 'WatchLessonCommentsItem' })

const { t, locale } = useI18n()
const watchStore = useWatchStore()

const props = defineProps<{
  label?: string
  nodes?: unknown[]
  depth?: number
  autor?: unknown
  updatedAt?: string
  deletedAt?: string
  createdAt?: string
  status?: string | number
  id?: string | number
  isMine?: boolean
  liked?: boolean
  likesCount?: number
  courseId?: string | number
  moduleId?: string | number
  lessonId?: string | number
}>()

const isTextAreaMyAnswerFocused = ref(false)
const isTextAreaToEditFocused = ref(false)
const showReplies = ref(false)
const visibleChars = ref(200)
const permission = ref(false)
const textAreaResp = ref(false)
const textAreaEdit = ref(false)
const editMyComment = ref('')
const myAnswer = ref('')
const isLoading = ref(false)

const commentMenuOptions = computed(() => {
  const options: Array<{ label: string; icon: string; value: string; color?: string }> = []
  if (props.isMine) {
    options.push({ label: t('comments.to_edit'), icon: 'edit', value: 'edit' })
  }
  if ((permission.value || props.isMine) && !props.deletedAt) {
    options.push({ label: t('comments.to_delete'), icon: 'delete', value: 'delete', color: 'var(--feedback-error-500)' })
  }
  if ((permission.value || props.isMine) && !!props.deletedAt) {
    options.push({ label: t('comments.to_restore'), icon: 'undo', value: 'restore' })
  }
  return options
})

function handleMenuSelect(option: { value: string }) {
  switch (option.value) {
    case 'edit':
      editComment(props.id, props.label)
      break
    case 'delete':
      handleDeleteComment(props.id)
      break
    case 'restore':
      doCommentRestore({ id: props.id })
      break
  }
}

const _isEdited = computed(() => {
  const createDate = new Date(props.createdAt!)
  const updatedDate = new Date(props.updatedAt!)
  return createDate < updatedDate
})

const commentTruncated = computed(() => {
  const text = props.label
  const maxLength = visibleChars.value
  if (!text) return ''
  if (text.length <= maxLength) return text
  const truncated = text.substring(0, maxLength).trim()
  return `${truncated}...`
})

function doCommentLike(payload: unknown) {
  return watchStore.doCommentLike(payload)
}

function doCommentUnlike(payload: unknown) {
  return watchStore.doCommentUnlike(payload)
}

function doCommentCreate(payload: unknown) {
  return watchStore.doCommentCreate(payload)
}

function doCommentApprove(payload: unknown) {
  return watchStore.doCommentApprove(payload)
}

function doCommentReprove(payload: unknown) {
  return watchStore.doCommentReprove(payload)
}

function doCommentUpdate(payload: unknown) {
  return watchStore.doCommentUpdate(payload)
}

function doCommentRestore(payload: unknown) {
  return watchStore.doCommentRestore(payload)
}

function handleCancelCommentEdition() {
  isTextAreaToEditFocused.value = !isTextAreaToEditFocused.value
  textAreaEdit.value = false
  editMyComment.value = ''
}

function handleCancelMyAnswer() {
  isTextAreaMyAnswerFocused.value = false
  textAreaResp.value = false
  myAnswer.value = ''
}

function toggleToAnswer() {
  textAreaResp.value = !textAreaResp.value
}

function toggleShowReplies() {
  showReplies.value = !showReplies.value
}

function showMore() {
  visibleChars.value = 2000
}

function showLess() {
  visibleChars.value = 200
}

function timeAgo(dateString: string | undefined): string {
  if (!dateString) return ''
  const dateFnsLocales: Record<string, unknown> = {
    pt: ptBR,
    en: enUS,
    es: es,
  }
  return formatDistanceToNow(new Date(dateString), {
    addSuffix: true,
    locale: dateFnsLocales[locale.value] || ptBR,
  })
}

async function answer(parent_lesson_comment_id: string | number | undefined) {
  isLoading.value = true
  try {
    await doCommentCreate({
      parent_lesson_comment_id,
      comment: myAnswer.value,
      courseId: props.courseId,
      moduleId: props.moduleId,
      lessonId: props.lessonId,
    })
  } catch {
    toast.events.emit('error', {
      message: t('comments.error_desc'),
      title: t('comments.error_title'),
    })
  } finally {
    handleCancelMyAnswer()
    isLoading.value = false
  }
}

async function editMyCommentFunc(id: string | number | undefined) {
  isLoading.value = true
  try {
    await doCommentUpdate({
      id,
      comment: editMyComment.value,
      courseId: props.courseId,
      moduleId: props.moduleId,
      lessonId: props.lessonId,
    })
  } catch {
    toast.events.emit('error', {
      message: t('comments.error_desc'),
      title: t('comments.error_title'),
    })
  } finally {
    editMyComment.value = ''
    textAreaEdit.value = false
    isLoading.value = false
  }
}

function editComment(_id: string | number | undefined, comentario: string | undefined) {
  textAreaEdit.value = true
  editMyComment.value = comentario || ''
}

function getPermission() {
  const permissionCookie = Cookies.get('access_level')
  const typeView = Cookies.get('type_student')
  if (permissionCookie === 'admin' || permissionCookie === 'owner') {
    if (typeView === '1') {
      permission.value = false
      return
    }
    permission.value = true
  } else {
    permission.value = false
  }
}

function handleDeleteComment(id: string | number | undefined) {
  modal.show('ModalDeleteComment', {
    item: id,
    courseId: props.courseId,
    moduleId: props.moduleId,
    lessonId: props.lessonId,
  })
}

onMounted(() => {
  getPermission()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', () => {})
})
</script>

<style lang="scss" scoped>
.comment {
	display: flex;
	align-items: flex-start;
	gap: 16px;
	position: relative;
	width: 100%;

	&__autor {
		width: 100%;

		display: flex;
		flex-direction: column;

		&__info {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 4px;

			&__more {
				padding: 8px;
				border-radius: 30px;

				&:hover {
					cursor: pointer;
					background-color: var(--badge-bg-default);
				}
			}
		}

		&__content {
			width: 100%;

			&__show-more,
			&__show-less {
				cursor: pointer;
			}

			&__to-answer,
			&__to-edit {
				display: flex;
				flex-direction: column;
				align-items: flex-end;
				gap: 16px;
				margin-top: 4px;

				&__actions {
					display: flex;
					align-items: center;
					gap: 16px;
				}
			}

			&__actions {
				display: flex;
				align-items: center;
				gap: 28px;
				margin-top: 16px;

				.likes {
					display: flex;
					align-items: center;
					gap: 2px;

					.isax {
						padding: 6px;
						border-radius: 30px;

						&:hover {
							background-color: var(--badge-bg-default);
							cursor: pointer;
						}
					}

					.text-play {
						min-width: 20px;
					}
				}

				.to-answer {
					padding: 8px 16px;
					border-radius: 30px;

					&:hover {
						background-color: var(--badge-bg-default);
						cursor: pointer;
					}
				}
			}

			&__replies {
				width: 100%;

				.see-replies {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 6px;
					padding: 8px 14px;
					border-radius: 30px;
					width: 126px;

					&:hover {
						background-color: var(--badge-bg-default);
						cursor: pointer;
					}
				}
			}
		}
	}
}
</style>
